import React, { useState, useSyncExternalStore } from 'react';
import { Cookie, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { subscribeAnalyticsConsent, readAnalyticsConsent, saveAnalyticsConsent } from '../lib/analyticsConsent';
import { isGa4Configured } from '../lib/ga4';
import { CookieBanner, consentButtonStyle } from './CookieBanner';
import { PRIVACY_PATH } from '../content/legal';

export const CookiePreferences = () => {
  const [open, setOpen] = useState(false);
  const choice = useSyncExternalStore(subscribeAnalyticsConsent, readAnalyticsConsent, () => null);
  const [error, setError] = useState('');

  const choose = (next) => {
    if (!saveAnalyticsConsent(next)) {
      setError('Analytics is off for this visit, but your browser could not save your preference.');
      return;
    }
    setError('');
    setOpen(false);
  };

  const changeOpen = (next) => {
    if (next) setError('');
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      {choice === null && !open && isGa4Configured() && <CookieBanner onChoose={choose} onDetails={() => changeOpen(true)} error={error} />}
      <DialogTrigger asChild>
        <button data-testid="cookie-settings-button" className="inline-flex items-center gap-2 rounded px-2 py-2 text-sm text-gray-300 underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
          <Cookie aria-hidden="true" className="h-4 w-4" /> Cookie settings
        </button>
      </DialogTrigger>
      <DialogContent data-testid="cookie-settings-dialog" closeTestId="cookie-settings-close" className="w-[calc(100%-2rem)] max-h-[85dvh] overflow-y-auto rounded-lg border-gray-200 bg-white text-left text-gray-900">
        <DialogHeader>
          <ShieldCheck aria-hidden="true" className="mb-2 h-6 w-6 text-[#7c4a97]" />
          <DialogTitle data-testid="cookie-settings-title" className="text-lg tracking-normal">Cookies &amp; analytics</DialogTitle>
          <DialogDescription data-testid="cookie-settings-description" className="text-sm text-gray-600">
            Analytics is optional. Your brochure access and enquiries do not depend on accepting it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm leading-relaxed">
          <p data-testid="cookie-preference-notice">We store your choice in this browser for up to six months. If you accept, Google Analytics also uses first-party cookies with a lifetime of up to six months.</p>
          <p data-testid="cookie-analytics-notice">With your permission, Google Analytics measures visits, traffic sources, device information and employer or university brochure clicks. We do not send enquiry fields to analytics. A click does not confirm that a file was saved.</p>
          <p data-testid="cookie-provider-notice">Advertising tracking is disabled. Google processes analytics data under its <a data-testid="google-privacy-policy-link" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7c4a97] underline underline-offset-2">privacy policy</a>.</p>
          <a data-testid="cookie-genmoney-privacy-link" href={PRIVACY_PATH} className="inline-block text-[#7c4a97] underline underline-offset-2">Privacy Notice</a>
          <p data-testid="cookie-choice-status" role="status" className="font-medium text-[#604f9b]">
            {!isGa4Configured() ? 'Analytics is unavailable.' : choice === 'granted' ? 'Saved preference: analytics allowed.' : choice === 'denied' ? 'Saved preference: analytics off.' : 'Analytics is off until you choose to accept.'}
          </p>
          <p data-testid="cookie-withdrawal-notice">Change your choice at any time using Cookie settings in the footer. Reject analytics withdraws acceptance and refreshes the page to stop tracking; unsent form entries may be lost. It does not erase earlier reports.</p>
        </div>
        {error && <p data-testid="cookie-storage-error" role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button data-testid="cookie-reject-button" className={consentButtonStyle} onClick={() => choose('denied')}>Reject analytics</Button>
          <Button data-testid="cookie-accept-button" className={consentButtonStyle} onClick={() => choose('granted')}>Accept analytics</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};